/* Bulk upload of ad hoc charges: choose the filled template (+ a ZIP of approval screenshots), Preview (nothing is
   saved), then "Yes, I accept" adds the rows that passed. The server validates every row again on accept. */
import React, { useState } from 'react'
import { Alert, Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle, Table, TableBody, TableCell, TableHead,
  TableRow, Typography } from '@mui/material'
import { LoadingButton } from '@mui/lab'
import { useSnackbar } from 'notistack'
import { useAdhocBulkMutation } from '../../../redux/ptlBillingApiSlice'
import { errText, inr } from '../billingUtils'

export default function AdhocBulkDialog({ onClose, onTemplate }) {
  const { enqueueSnackbar } = useSnackbar()
  const [bulk, { isLoading }] = useAdhocBulkMutation()
  const [file, setFile] = useState(null)
  const [proofs, setProofs] = useState(null)
  const [result, setResult] = useState(null)
  const [err, setErr] = useState(null)

  const send = async (confirm) => {
    setErr(null)
    const res = await bulk({ file, proofs, confirm })
    if (res.error) return setErr(errText(res.error))
    if (confirm) {
      enqueueSnackbar(`${res.data.savedRows} ad hoc charges added, ${res.data.skippedRows} skipped.`, { variant: 'success' })
      setResult(res.data)
      return
    }
    setResult(res.data)
  }

  return (
    <Dialog open onClose={() => onClose(!!result?.accepted)} maxWidth='md' fullWidth>
      <DialogTitle>
        Bulk Upload: Ad hoc Charges
        <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>Check what you are uploading before it is added.</Typography>
      </DialogTitle>
      <DialogContent sx={{ display: 'grid', gap: 2 }}>
        <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', alignItems: 'center' }}>
          <Button variant='outlined' component='label' size='small' sx={{ textTransform: 'none' }}>
            {file ? file.name : 'Choose filled template (.xlsx)'}
            <input hidden type='file' accept='.xlsx' onChange={(e) => { setFile(e.target.files?.[0] || null); setResult(null) }} />
          </Button>
          <Button variant='outlined' component='label' size='small' sx={{ textTransform: 'none' }}>
            {proofs ? proofs.name : 'Approval screenshots (.zip, optional)'}
            <input hidden type='file' accept='.zip' onChange={(e) => { setProofs(e.target.files?.[0] || null); setResult(null) }} />
          </Button>
          <Button size='small' onClick={onTemplate} sx={{ textTransform: 'none' }}>Download template</Button>
        </Box>
        <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>
          Approval screenshots go in the ZIP, named as in the "Approval File Name" column. Only KAM can fill the client amount and only PTL Ops the courier amount.
        </Typography>
        {err && <Alert severity='error'>{err}</Alert>}
        {result && (
          <>
            <Alert severity={result.accepted ? 'success' : 'info'}>
              {result.accepted ? `${result.savedRows} rows added, ${result.skippedRows} skipped.` : `${result.okRows} rows will be added, ${result.skippedRows} skipped.`}
            </Alert>
            <Table size='small'>
              <TableHead>
                <TableRow>
                  <TableCell>Row</TableCell><TableCell>AWB</TableCell><TableCell>Client</TableCell><TableCell>Charge type</TableCell>
                  <TableCell align='right'>Client (KAM)</TableCell><TableCell>Approval file</TableCell><TableCell align='right'>Courier (Ops)</TableCell><TableCell>Result</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {result.rows.map((r) => (
                  <TableRow key={r.row} sx={r.ok ? undefined : { bgcolor: '#FFF6F5' }}>
                    <TableCell>{r.row}</TableCell><TableCell>{r.key || '—'}</TableCell><TableCell>{r.merchantName || '—'}</TableCell><TableCell>{r.chargeType || '—'}</TableCell>
                    <TableCell align='right'>{r.clientAmount != null ? inr(r.clientAmount) : '—'}</TableCell><TableCell>{r.approvalFile || '—'}</TableCell>
                    <TableCell align='right'>{r.courierAmount != null ? inr(r.courierAmount) : '—'}</TableCell>
                    <TableCell sx={{ fontSize: 12 }}>
                      <Chip size='small' color={r.ok ? 'success' : 'error'} label={r.ok ? (result.accepted ? 'ADDED' : 'OK') : 'SKIP'} sx={{ mr: 1 }} />{r.message}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={() => onClose(!!result?.accepted)}>{result?.accepted ? 'Close' : 'Cancel'}</Button>
        {!result?.accepted && (
          <LoadingButton variant='outlined' loading={isLoading} disabled={!file} onClick={() => send(false)}>Preview</LoadingButton>
        )}
        {result && !result.accepted && (
          <LoadingButton variant='contained' color='success' loading={isLoading} disabled={!result.okRows} onClick={() => send(true)}>
            Yes, I accept ({result.okRows} rows)
          </LoadingButton>
        )}
      </DialogActions>
    </Dialog>
  )
}
